import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Package, AlertTriangle, PackageX, IndianRupee, TrendingDown, TrendingUp } from 'lucide-react';
import { clsx } from 'clsx';
import { useTheme } from '../../contexts/ThemeContext';
import { SkeletonTable, EmptyState } from '../../components/LoadingStates';
import { inventoryReportService } from '../../services/inventory/inventoryReport';
import { unitShort, stockStatus } from '../../types/inventory';
import { formatCurrency, formatQty } from './inventoryUtils';
import { Th } from './StockItemsTab';

interface Props { org: string; restaurantId: string; }

type ReportView = 'overview' | 'valuation' | 'profitability' | 'consumption';

export const InventoryReportsTab: React.FC<Props> = ({ org, restaurantId }) => {
  const { currentTheme } = useTheme();
  const [view, setView] = useState<ReportView>('overview');

  // Default consumption range: last 30 days
  const today = new Date();
  const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
  const [start, setStart] = useState(monthAgo.toISOString().slice(0, 10));
  const [end, setEnd] = useState(today.toISOString().slice(0, 10));

  const { data: summary } = useQuery(['inv-report-summary', org, restaurantId], () => inventoryReportService.getSummary(org, restaurantId));
  const { data: lowStock, isLoading: lowLoading } = useQuery(['inv-report-low', org, restaurantId], () => inventoryReportService.getLowStock(org, restaurantId));
  const { data: valuation, isLoading: valLoading } = useQuery(['inv-report-valuation', org, restaurantId], () => inventoryReportService.getValuation(org, restaurantId), { enabled: view === 'valuation' });
  const { data: profitability, isLoading: profLoading } = useQuery(['inv-report-profitability', org, restaurantId], () => inventoryReportService.getProfitability(org, restaurantId), { enabled: view === 'profitability' });
  const { data: consumption, isLoading: conLoading } = useQuery(
    ['inv-report-consumption', org, restaurantId, start, end],
    () => inventoryReportService.getConsumption({ org, restaurantId, start, end }),
    { enabled: view === 'consumption' },
  );

  const valuationTotal = valuation?.reduce((s, r) => s + r.stockValue, 0) ?? 0;

  const tableWrap = { backgroundColor: currentTheme.colors.background.card, borderColor: currentTheme.colors.border.primary };

  return (
    <div>
      {/* Summary cards (always visible) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card icon={<Package className="h-5 w-5" />} label="Total Items" value={summary ? String(summary.totalItems) : '—'} bg="#eef2ff" fg="#4f46e5" />
        <Card icon={<AlertTriangle className="h-5 w-5" />} label="Low Stock" value={summary ? String(summary.lowStockCount) : '—'} bg="#fef3c7" fg="#b45309" />
        <Card icon={<PackageX className="h-5 w-5" />} label="Out of Stock" value={summary ? String(summary.outOfStockCount) : '—'} bg="#fee2e2" fg="#dc2626" />
        <Card icon={<IndianRupee className="h-5 w-5" />} label="Stock Value" value={summary ? formatCurrency(summary.totalStockValue) : '—'} bg="#dcfce7" fg="#16a34a" />
      </div>

      {/* Report selector */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {([['overview', 'Low Stock'], ['valuation', 'Stock Valuation'], ['profitability', 'Menu Profitability'], ['consumption', 'Consumption']] as [ReportView, string][]).map(([key, label]) => (
          <button key={key} onClick={() => setView(key)}
            className="px-4 py-2 rounded-lg text-sm font-medium border transition-colors"
            style={{
              backgroundColor: view === key ? currentTheme.colors.primary[600] : currentTheme.colors.background.card,
              color: view === key ? '#fff' : currentTheme.colors.text.secondary,
              borderColor: view === key ? currentTheme.colors.primary[600] : currentTheme.colors.border.primary,
            }}>
            {label}
          </button>
        ))}
      </div>

      {/* Low stock / overview */}
      {view === 'overview' && (
        lowLoading ? <SkeletonTable rows={5} columns={4} /> : !lowStock || lowStock.length === 0 ? (
          <EmptyState icon={<AlertTriangle className="h-14 w-14" style={{ color: currentTheme.colors.text.secondary }} />} title="All stocked up" description="No items are below their reorder level." />
        ) : (
          <div className="overflow-x-auto rounded-xl border" style={tableWrap}>
            <table className="min-w-full divide-y" style={{ borderColor: currentTheme.colors.border.primary }}>
              <thead className="ui-thead">
                <tr><Th>Item</Th><Th className="text-right">Current</Th><Th className="text-right">Reorder Level</Th><Th className="text-right">Shortfall</Th><Th>Status</Th></tr>
              </thead>
              <tbody className="ui-tbody">
                {lowStock.map(i => {
                  const status = stockStatus(i.currentStock, i.reorderLevel);
                  const shortfall = Math.max(0, (i.parLevel || i.reorderLevel) - i.currentStock);
                  return (
                    <tr key={i.id}>
                      <td className="px-4 py-3 text-sm font-medium" style={{ color: currentTheme.colors.text.primary }}>{i.name}</td>
                      <td className="px-4 py-3 text-sm text-right" style={{ color: currentTheme.colors.text.primary }}>{formatQty(i.currentStock)} {unitShort(i.unit)}</td>
                      <td className="px-4 py-3 text-sm text-right" style={{ color: currentTheme.colors.text.secondary }}>{formatQty(i.reorderLevel)} {unitShort(i.unit)}</td>
                      <td className="px-4 py-3 text-sm text-right font-medium" style={{ color: '#b45309' }}>{formatQty(shortfall)} {unitShort(i.unit)}</td>
                      <td className="px-4 py-3">
                        <span className={clsx('inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium', status === 'OUT' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800')}>
                          {status === 'OUT' ? 'Out of Stock' : 'Low Stock'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* Valuation */}
      {view === 'valuation' && (
        valLoading ? <SkeletonTable rows={6} columns={5} /> : !valuation || valuation.length === 0 ? (
          <EmptyState icon={<IndianRupee className="h-14 w-14" style={{ color: currentTheme.colors.text.secondary }} />} title="No stock to value" description="Add stock items to see their valuation." />
        ) : (
          <div className="overflow-x-auto rounded-xl border" style={tableWrap}>
            <table className="min-w-full divide-y" style={{ borderColor: currentTheme.colors.border.primary }}>
              <thead className="ui-thead">
                <tr><Th>Item</Th><Th>Category</Th><Th className="text-right">Stock</Th><Th className="text-right">Cost / Unit</Th><Th className="text-right">Value</Th></tr>
              </thead>
              <tbody className="ui-tbody">
                {valuation.map(r => (
                  <tr key={r.inventoryItemId}>
                    <td className="px-4 py-3 text-sm font-medium" style={{ color: currentTheme.colors.text.primary }}>{r.itemName}</td>
                    <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.secondary }}>{r.category || '—'}</td>
                    <td className="px-4 py-3 text-sm text-right" style={{ color: currentTheme.colors.text.primary }}>{formatQty(r.currentStock)} {unitShort(r.unit)}</td>
                    <td className="px-4 py-3 text-sm text-right" style={{ color: currentTheme.colors.text.secondary }}>{formatCurrency(r.costPerUnit)}</td>
                    <td className="px-4 py-3 text-sm text-right font-medium" style={{ color: currentTheme.colors.text.primary }}>{formatCurrency(r.stockValue)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ backgroundColor: currentTheme.colors.background.secondary }}>
                  <td className="px-4 py-3 text-sm font-semibold" colSpan={4} style={{ color: currentTheme.colors.text.primary }}>Total Valuation</td>
                  <td className="px-4 py-3 text-sm text-right font-bold" style={{ color: currentTheme.colors.primary[700] }}>{formatCurrency(valuationTotal)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )
      )}

      {/* Menu profitability / food-cost */}
      {view === 'profitability' && (
        profLoading ? <SkeletonTable rows={6} columns={6} /> : !profitability || profitability.length === 0 ? (
          <EmptyState icon={<TrendingUp className="h-14 w-14" style={{ color: currentTheme.colors.text.secondary }} />} title="No menu items" description="Add menu items and recipes to see food cost and margins." />
        ) : (
          <div>
            <p className="text-sm mb-3" style={{ color: currentTheme.colors.text.secondary }}>
              Food cost &amp; margin per dish. Items without a recipe show price only — add a recipe to cost them. Lowest-margin dishes are listed first.
            </p>
            <div className="overflow-x-auto rounded-xl border" style={tableWrap}>
              <table className="min-w-full divide-y" style={{ borderColor: currentTheme.colors.border.primary }}>
                <thead className="ui-thead">
                  <tr>
                    <Th>Dish</Th>
                    <Th className="text-right">Price</Th>
                    <Th className="text-right">Food Cost</Th>
                    <Th className="text-right">Food Cost %</Th>
                    <Th className="text-right">Margin</Th>
                    <Th className="text-right">Margin %</Th>
                  </tr>
                </thead>
                <tbody className="ui-tbody">
                  {profitability.map(r => {
                    // Food cost % health: <=35% good, <=50% ok, else high
                    const fc = r.foodCostPercent;
                    const fcColor = !r.hasRecipe ? currentTheme.colors.text.secondary : fc <= 35 ? '#16a34a' : fc <= 50 ? '#b45309' : '#dc2626';
                    return (
                      <tr key={r.menuItemId}>
                        <td className="px-4 py-3 text-sm font-medium" style={{ color: currentTheme.colors.text.primary }}>
                          {r.itemName}
                          {!r.hasRecipe && <span className="ml-2 inline-flex px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-500">no recipe</span>}
                        </td>
                        <td className="px-4 py-3 text-sm text-right" style={{ color: currentTheme.colors.text.primary }}>{formatCurrency(r.sellingPrice)}</td>
                        <td className="px-4 py-3 text-sm text-right" style={{ color: currentTheme.colors.text.secondary }}>{r.hasRecipe ? formatCurrency(r.recipeCost) : '—'}</td>
                        <td className="px-4 py-3 text-sm text-right font-medium" style={{ color: fcColor }}>{r.hasRecipe ? `${r.foodCostPercent.toFixed(1)}%` : '—'}</td>
                        <td className="px-4 py-3 text-sm text-right" style={{ color: currentTheme.colors.text.primary }}>{r.hasRecipe ? formatCurrency(r.margin) : '—'}</td>
                        <td className="px-4 py-3 text-sm text-right font-medium" style={{ color: r.hasRecipe ? (r.marginPercent >= 50 ? '#16a34a' : r.marginPercent >= 30 ? '#b45309' : '#dc2626') : currentTheme.colors.text.secondary }}>
                          {r.hasRecipe ? `${r.marginPercent.toFixed(1)}%` : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )
      )}

      {/* Consumption */}
      {view === 'consumption' && (
        <div>
          <div className="flex flex-wrap items-end gap-3 mb-4">
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: currentTheme.colors.text.secondary }}>From</label>
              <input type="date" value={start} onChange={e => setStart(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: currentTheme.colors.text.secondary }}>To</label>
              <input type="date" value={end} onChange={e => setEnd(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
          </div>
          {conLoading ? <SkeletonTable rows={6} columns={5} /> : !consumption || consumption.length === 0 ? (
            <EmptyState icon={<TrendingDown className="h-14 w-14" style={{ color: currentTheme.colors.text.secondary }} />} title="No movement in this period" description="Try a wider date range." />
          ) : (
            <div className="overflow-x-auto rounded-xl border" style={tableWrap}>
              <table className="min-w-full divide-y" style={{ borderColor: currentTheme.colors.border.primary }}>
                <thead className="ui-thead">
                  <tr><Th>Item</Th><Th className="text-right">Purchased</Th><Th className="text-right">Consumed</Th><Th className="text-right">Wastage</Th></tr>
                </thead>
                <tbody className="ui-tbody">
                  {consumption.map(r => (
                    <tr key={r.inventoryItemId}>
                      <td className="px-4 py-3 text-sm font-medium" style={{ color: currentTheme.colors.text.primary }}>{r.itemName}</td>
                      <td className="px-4 py-3 text-sm text-right" style={{ color: '#16a34a' }}>{formatQty(r.purchased)} {unitShort(r.unit)}</td>
                      <td className="px-4 py-3 text-sm text-right" style={{ color: currentTheme.colors.text.primary }}>{formatQty(r.consumed)} {unitShort(r.unit)}</td>
                      <td className="px-4 py-3 text-sm text-right" style={{ color: '#dc2626' }}>{formatQty(r.wastage)} {unitShort(r.unit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Card: React.FC<{ icon: React.ReactNode; label: string; value: string; bg: string; fg: string }> = ({ icon, label, value, bg, fg }) => {
  const { currentTheme } = useTheme();
  return (
    <div className="rounded-2xl border shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_30px_-16px_rgba(16,24,40,0.20)] p-4 flex items-center gap-3" style={{ backgroundColor: currentTheme.colors.background.card, borderColor: currentTheme.colors.border.primary }}>
      <div className="p-2.5 rounded-lg flex-shrink-0" style={{ backgroundColor: bg, color: fg }}>{icon}</div>
      <div className="min-w-0">
        <p className="text-xs truncate" style={{ color: currentTheme.colors.text.secondary }}>{label}</p>
        <p className="text-lg font-bold truncate" style={{ color: currentTheme.colors.text.primary }}>{value}</p>
      </div>
    </div>
  );
};
